import React, { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Brand } from "@/components/Brand";
import { api, setAuthToken } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast, Toaster } from "sonner";
import { CheckCircle2, Search, X, Loader2, Sparkles, AlertTriangle, RefreshCw, ShieldCheck } from "lucide-react";

/* ============ AdminLogin ============ */
export function AdminLogin() {
  const [email, setEmail] = useState("admin@erp-institute.net");
  const [pw, setPw] = useState("");
  const [loading, setLoading] = useState(false);
  const nav = useNavigate();
  async function submit(e) {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/admin/login", { email, password: pw });
      setAuthToken(data.token);
      nav("/admin");
    } catch { toast.error("Invalid credentials"); }
    finally { setLoading(false); }
  }
  return (
    <div className="min-h-screen"><Brand /><Toaster richColors position="top-center" />
      <main className="mx-auto max-w-md px-6 py-24">
        <h1 className="text-2xl font-semibold">Admin login</h1>
        <form onSubmit={submit} className="mt-6 space-y-4">
          <div><Label>Email</Label><Input data-testid="admin-email" className="mt-1" value={email} onChange={e => setEmail(e.target.value)} /></div>
          <div><Label>Password</Label><Input data-testid="admin-password" type="password" className="mt-1" value={pw} onChange={e => setPw(e.target.value)} /></div>
          <Button data-testid="admin-login-submit" type="submit" disabled={loading}>Sign in</Button>
        </form>
      </main>
    </div>
  );
}

/* ============ Sections ============ */
const SECTIONS = [
  { key: "analytics",          label: "Analytics",          kind: "custom" },
  { key: "validation",         label: "Validation",         kind: "custom" },
  { key: "reimport",           label: "Re-Import",          kind: "custom" },
  { key: "leads",              label: "Leads",              kind: "custom" },
  { key: "sap_module",         label: "SAP Modules" },
  { key: "career_path",        label: "Career Paths" },
  { key: "certification",      label: "Certifications" },
  { key: "course",             label: "Courses" },
  { key: "unit",               label: "Units",              highlight: "keyword_auto" },
  { key: "skill",              label: "Skills" },
  { key: "profession_mapping", label: "Profession Mappings" },
  { key: "job_role",           label: "Job Roles",          highlight: "keyword_auto" },
  { key: "employer_example",   label: "Employers" },
  { key: "course_product",     label: "Course Products", highlight: "new" },
  { key: "level_profile",      label: "Level Profiles" },
  { key: "freelancer_rate",    label: "Freelancer Rates" },
  { key: "ai_suggestions",     label: "AI Suggestions" },
];

// Field → referenced collection (for typeahead)
const REF_FIELDS = {
  module_id: "sap_module",
  career_path_id: "career_path",
  certification_id: "certification",
  course_id: "course",
  next_role_id: "job_role",
  primary_career_path_id: "career_path",
  secondary_career_path_ids: "career_path",       // array
  required_sap_skill_ids: "skill",                 // array
  taught_skill_ids: "skill",                       // array
  skill_ids: "skill",                              // in embedded topics
  related_course_ids: "course",                    // array
};

/* ============ Dashboard ============ */
export function AdminDashboard() {
  const [me, setMe] = useState(null);
  const [active, setActive] = useState("analytics");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState({ review_status: "", skill_match_method: "", q: "" });
  const [editing, setEditing] = useState(null);   // row being edited
  const [showChangePw, setShowChangePw] = useState(false);
  const nav = useNavigate();

  useEffect(() => {
    api.get("/admin/me").then(r => setMe(r.data)).catch(() => nav("/admin/login"));
  }, [nav]);

  const activeMeta = useMemo(() => SECTIONS.find(s => s.key === active), [active]);

  useEffect(() => {
    if (!me) return;
    setData(null);   // reset before switching to avoid rendering wrong shape
    setLoading(true);
    let url;
    if (active === "analytics") url = "/admin/analytics";
    else if (active === "validation") url = "/admin/validation";
    else if (active === "reimport") { setLoading(false); return; }
    else if (active === "leads") { setLoading(false); return; }
    else {
      const p = new URLSearchParams();
      if (filter.review_status) p.set("review_status", filter.review_status);
      if (filter.skill_match_method) p.set("skill_match_method", filter.skill_match_method);
      url = `/admin/${active}${p.toString() ? "?" + p.toString() : ""}`;
    }
    api.get(url).then(r => setData(r.data)).finally(() => setLoading(false));
  }, [active, me, filter.review_status, filter.skill_match_method]);

  if (!me) return <div className="min-h-screen"><Brand /></div>;

  function reload() {
    if (activeMeta?.kind === "custom") return;
    setLoading(true);
    const p = new URLSearchParams();
    if (filter.review_status) p.set("review_status", filter.review_status);
    if (filter.skill_match_method) p.set("skill_match_method", filter.skill_match_method);
    api.get(`/admin/${active}${p.toString() ? "?" + p.toString() : ""}`)
       .then(r => setData(r.data)).finally(() => setLoading(false));
  }

  return (
    <div className="min-h-screen"><Brand /><Toaster richColors position="top-center" />
      <div className="mx-auto max-w-[1400px] px-4 md:px-6 py-6 grid grid-cols-12 gap-6">
        <aside className="col-span-12 md:col-span-3 lg:col-span-2">
          <div className="rounded-xl border border-border p-2 bg-card sticky top-24">
            {SECTIONS.map(s => (
              <button key={s.key} data-testid={`admin-nav-${s.key}`}
                onClick={() => { setActive(s.key); setFilter({ review_status: "", skill_match_method: "", q: "" }); }}
                className={`w-full text-left px-3 py-2 rounded-md text-sm transition-colors ${active === s.key ? "bg-primary text-primary-foreground" : "hover:bg-muted"}`}>
                {s.label}
              </button>
            ))}
            <button onClick={() => setShowChangePw(true)} data-testid="admin-change-password"
                    className="w-full text-left px-3 py-2 mt-2 border-t border-border rounded-md text-xs text-muted-foreground hover:text-foreground">Passwort ändern</button>
            <button onClick={() => { setAuthToken(null); nav("/admin/login"); }} data-testid="admin-logout"
                    className="w-full text-left px-3 py-2 rounded-md text-xs text-muted-foreground hover:text-foreground">Log out</button>
          </div>
        </aside>

        <main className="col-span-12 md:col-span-9 lg:col-span-10 min-w-0">
          <div className="flex items-baseline justify-between flex-wrap gap-3">
            <h1 className="text-2xl font-semibold capitalize">{activeMeta?.label || active}</h1>
            {activeMeta && !activeMeta.kind && (
              <div className="flex items-center gap-2 flex-wrap">
                <FilterPill label="all" active={!filter.review_status} onClick={() => setFilter(f => ({ ...f, review_status: "" }))}/>
                <FilterPill label="generated" active={filter.review_status === "generated"} onClick={() => setFilter(f => ({ ...f, review_status: "generated" }))}/>
                <FilterPill label="approved" active={filter.review_status === "approved"} onClick={() => setFilter(f => ({ ...f, review_status: "approved" }))}/>
                {activeMeta.highlight === "keyword_auto" && (
                  <FilterPill label="only keyword_auto" tone="amber"
                              active={filter.skill_match_method === "keyword_auto"}
                              onClick={() => setFilter(f => ({ ...f, skill_match_method: f.skill_match_method === "keyword_auto" ? "" : "keyword_auto" }))}/>
                )}
                <div className="relative">
                  <Search className="h-3.5 w-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground"/>
                  <Input data-testid="admin-search" className="h-8 pl-8 w-56 text-xs" placeholder="filter…" value={filter.q}
                         onChange={e => setFilter(f => ({ ...f, q: e.target.value }))}/>
                </div>
              </div>
            )}
          </div>
          <div className="mt-4">
            {loading && <div className="text-sm text-muted-foreground">Loading…</div>}
            {!loading && active === "analytics" && data && <Analytics data={data} />}
            {!loading && active === "validation" && data && <Validation data={data} />}
            {active === "reimport" && <ReImportPanel onDone={() => { toast.success("Re-imported"); }}/>}
            {active === "leads" && <LeadsPanel />}
            {!loading && !activeMeta?.kind && Array.isArray(data) &&
              <DataTable collection={active} rows={data.filter(r => matchesQ(r, filter.q))} onEdit={setEditing} onApprove={async (id) => {
                await api.post(`/admin/${active}/${id}/approve`);
                toast.success("Approved");
                reload();
              }}/>}
          </div>
        </main>
      </div>

      {editing && (
        <EditDialog
          collection={active}
          row={editing}
          onClose={() => setEditing(null)}
          onSaved={() => { toast.success("Saved"); setEditing(null); reload(); }}
        />
      )}
      {showChangePw && <ChangePasswordDialog onClose={() => setShowChangePw(false)} />}
    </div>
  );
}

function ChangePasswordDialog({ onClose }) {
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  async function submit(e) {
    e.preventDefault();
    if (next.length < 8) { toast.error("Neues Passwort muss mindestens 8 Zeichen haben"); return; }
    if (next !== confirm) { toast.error("Passwörter stimmen nicht überein"); return; }
    setLoading(true);
    try {
      await api.post("/admin/change-password", { current_password: current, new_password: next });
      toast.success("Passwort geändert");
      onClose();
    } catch (err) {
      const msg = err?.response?.data?.detail || "Fehler beim Ändern";
      toast.error(msg);
    } finally { setLoading(false); }
  }
  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent data-testid="change-password-dialog" className="max-w-md">
        <DialogHeader>
          <DialogTitle>Passwort ändern</DialogTitle>
          <DialogDescription>Gib dein aktuelles Passwort und ein neues Passwort (min. 8 Zeichen) ein.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div>
            <Label>Aktuelles Passwort</Label>
            <Input data-testid="cpw-current" type="password" autoComplete="current-password" value={current} onChange={e => setCurrent(e.target.value)} required />
          </div>
          <div>
            <Label>Neues Passwort</Label>
            <Input data-testid="cpw-new" type="password" autoComplete="new-password" value={next} onChange={e => setNext(e.target.value)} minLength={8} required />
          </div>
          <div>
            <Label>Neues Passwort bestätigen</Label>
            <Input data-testid="cpw-confirm" type="password" autoComplete="new-password" value={confirm} onChange={e => setConfirm(e.target.value)} minLength={8} required />
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose} data-testid="cpw-cancel">Abbrechen</Button>
            <Button type="submit" disabled={loading} data-testid="cpw-submit">{loading ? "Speichert…" : "Ändern"}</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

/* ============ Helpers ============ */
function matchesQ(row, q) {
  if (!q) return true;
  const s = q.toLowerCase();
  return Object.entries(row).some(([, v]) => {
    if (typeof v === "string") return v.toLowerCase().includes(s);
    if (typeof v === "number") return String(v).includes(s);
    return false;
  });
}

function FilterPill({ label, active, onClick, tone }) {
  const cls = active
    ? (tone === "amber" ? "border-amber-500/50 bg-amber-500/10 text-amber-700 dark:text-amber-300" : "bg-primary text-primary-foreground border-primary")
    : "border-border hover:border-primary/50";
  return (
    <button data-testid={`filter-${label.replace(/\s+/g,'-')}`} onClick={onClick}
      className={`px-3 py-1 rounded-full border text-xs font-medium transition-colors ${cls}`}>
      {label}
    </button>
  );
}

function Analytics({ data }) {
  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
      <Stat label="Assessments" value={data.total_assessments} />
      <Stat label="Reports" value={data.total_reports} />
      {(data.events || []).map(e => <Stat key={e.event} label={e.event} value={e.count} />)}
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div className="rounded-xl border border-border p-4 bg-card">
      <div className="text-[10px] uppercase tracking-[0.24em] text-muted-foreground">{label}</div>
      <div className="mt-1 text-2xl font-semibold">{value}</div>
    </div>
  );
}

function Validation({ data }) {
  if (!data || !data.stats) return <div className="text-sm text-muted-foreground">Loading…</div>;
  const counts = data.counts_by_severity || { error: 0, warn: 0 };
  return (
    <div>
      <div className={`rounded-xl border p-4 ${data.ok ? "border-emerald-500/50 bg-emerald-500/5" : "border-rose-500/50 bg-rose-500/5"}`}>
        <div className="font-semibold inline-flex items-center gap-2">
          {data.ok ? <ShieldCheck className="h-4 w-4 text-emerald-600"/> : <AlertTriangle className="h-4 w-4 text-rose-600"/>}
          {data.ok ? "All checks passed" : "Errors found"}
        </div>
        <div className="text-xs text-muted-foreground mt-1">Errors: {counts.error} · Warnings: {counts.warn}</div>
      </div>
      <div className="mt-6 grid grid-cols-2 md:grid-cols-5 gap-3">
        {Object.entries(data.stats).map(([k, v]) => (
          <div key={k} className="rounded-lg border border-border p-3 text-sm bg-card">
            <div className="text-[10px] uppercase tracking-[0.24em] text-muted-foreground">{k}</div>
            <div className="mt-1 font-semibold">{v}</div>
          </div>
        ))}
      </div>
      {(data.issues || []).length > 0 && (
        <ul className="mt-6 space-y-1 text-sm max-h-[500px] overflow-auto pr-2">
          {data.issues.map((i, k) => (
            <li key={k} className={`px-3 py-2 rounded border ${i.level === "error" ? "border-rose-500/40 text-rose-700 dark:text-rose-300" : "border-amber-500/40 text-amber-700 dark:text-amber-300"}`}>
              [{i.level}] <span className="text-xs uppercase tracking-widest mr-2">{i.type}</span> {i.message}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/* v2.14 — Leads Panel: list + filters + detail + status/notes + delete + CSV export */
function LeadsPanel() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(null);
  const [filters, setFilters] = useState({ q: "", status: "", career_path_id: "", goal: "", has_purchase: "", has_waitlist: "" });
  const [backfilling, setBackfilling] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const p = new URLSearchParams();
      Object.entries(filters).forEach(([k, v]) => { if (v !== "" && v !== null) p.set(k, v); });
      const { data } = await api.get(`/admin/leads?${p.toString()}`);
      setRows(data.items || []);
    } catch { toast.error("Failed to load leads"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filters.status, filters.career_path_id, filters.goal, filters.has_purchase, filters.has_waitlist]);

  const exportCsv = async () => {
    try {
      const res = await api.get("/admin/leads/export.csv", { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "text/csv" }));
      const a = document.createElement("a"); a.href = url; a.download = "leads.csv"; a.click(); URL.revokeObjectURL(url);
    } catch { toast.error("Export failed"); }
  };
  const backfill = async () => {
    setBackfilling(true);
    try {
      const { data } = await api.post("/admin/leads/backfill");
      toast.success(data.skipped ? "Already backfilled" : `Backfilled ${data.backfilled} leads`);
      load();
    } catch { toast.error("Backfill failed"); }
    finally { setBackfilling(false); }
  };

  return (
    <div data-testid="leads-panel">
      <div className="flex flex-wrap items-center gap-2 mb-4">
        <Input data-testid="leads-search" placeholder="Search email/name/notes…" value={filters.q}
               onChange={e => setFilters({ ...filters, q: e.target.value })}
               onKeyDown={e => { if (e.key === "Enter") load(); }} className="max-w-xs"/>
        <Select value={filters.status || "all"} onValueChange={v => setFilters({ ...filters, status: v === "all" ? "" : v })}>
          <SelectTrigger className="w-36" data-testid="leads-filter-status"><SelectValue placeholder="Status"/></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            <SelectItem value="new">new</SelectItem>
            <SelectItem value="contacted">contacted</SelectItem>
            <SelectItem value="trial">trial</SelectItem>
            <SelectItem value="customer">customer</SelectItem>
            <SelectItem value="closed">closed</SelectItem>
          </SelectContent>
        </Select>
        <Select value={filters.has_purchase || "all"} onValueChange={v => setFilters({ ...filters, has_purchase: v === "all" ? "" : v })}>
          <SelectTrigger className="w-36" data-testid="leads-filter-purchase"><SelectValue placeholder="Purchase"/></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Any</SelectItem>
            <SelectItem value="true">Has purchase</SelectItem>
          </SelectContent>
        </Select>
        <Button variant="outline" onClick={load} data-testid="leads-reload">Reload</Button>
        <Button variant="outline" onClick={exportCsv} data-testid="leads-export">Export CSV</Button>
        <Button variant="outline" onClick={backfill} disabled={backfilling} data-testid="leads-backfill">
          {backfilling ? "Backfilling…" : "Backfill"}
        </Button>
        <div className="text-xs text-muted-foreground ml-auto">{rows.length} leads</div>
      </div>

      {loading ? (
        <div className="text-sm text-muted-foreground">Loading…</div>
      ) : (
        <div className="rounded-xl border border-border overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-muted/40 text-left text-xs uppercase tracking-widest text-muted-foreground">
              <tr>
                <th className="px-3 py-2">Email</th>
                <th className="px-3 py-2">Name</th>
                <th className="px-3 py-2">Path</th>
                <th className="px-3 py-2">Goal</th>
                <th className="px-3 py-2">Score</th>
                <th className="px-3 py-2">Status</th>
                <th className="px-3 py-2">Source</th>
                <th className="px-3 py-2">Rev.</th>
                <th className="px-3 py-2">CV</th>
                <th className="px-3 py-2">Updated</th>
              </tr>
            </thead>
            <tbody>
              {rows.map(l => (
                <tr key={l.id} className="border-t border-border hover:bg-muted/30 cursor-pointer"
                    onClick={() => setSelected(l.id)} data-testid={`lead-row-${l.email}`}>
                  <td className="px-3 py-2 font-mono text-xs">{l.email}</td>
                  <td className="px-3 py-2">{l.first_name || "—"}</td>
                  <td className="px-3 py-2">{l.career_path_id || "—"}</td>
                  <td className="px-3 py-2">{l.goal || "—"}</td>
                  <td className="px-3 py-2 tabular-nums">{l.match_score ?? "—"}</td>
                  <td className="px-3 py-2"><span className={`inline-block rounded-full px-2 py-0.5 text-[10px] font-semibold ${l.status === "customer" ? "bg-emerald-500/20 text-emerald-600" : l.status === "trial" ? "bg-primary/20 text-primary" : "bg-muted"}`}>{l.status}</span></td>
                  <td className="px-3 py-2 text-xs">{l.utm_campaign || "—"}</td>
                  <td className="px-3 py-2">{l.has_purchase ? "💰" : l.has_trial ? "🎫" : "—"}</td>
                  <td className="px-3 py-2">{l.has_cv ? "📎" : "—"}</td>
                  <td className="px-3 py-2 text-xs text-muted-foreground">{l.updated_at?.slice(0, 10)}</td>
                </tr>
              ))}
              {rows.length === 0 && <tr><td colSpan={10} className="px-3 py-8 text-center text-sm text-muted-foreground">No leads yet — the first assessment completion will create one.</td></tr>}
            </tbody>
          </table>
        </div>
      )}

      {selected && <LeadDetail email={selected} onClose={() => setSelected(null)} onChanged={load}/>}
    </div>
  );
}

function LeadDetail({ email, onClose, onChanged }) {
  const [lead, setLead] = useState(null);
  const [notes, setNotes] = useState("");
  const [status, setStatus] = useState("");
  useEffect(() => {
    api.get(`/admin/leads/${encodeURIComponent(email)}`).then(r => {
      setLead(r.data.lead);
      setNotes(r.data.lead.notes || "");
      setStatus(r.data.lead.status || "new");
    });
  }, [email]);
  const save = async () => {
    await api.patch(`/admin/leads/${encodeURIComponent(email)}`, { status, notes });
    toast.success("Lead updated");
    onChanged?.();
  };
  const del = async () => {
    if (!window.confirm(`GDPR: This will permanently delete ${email} incl. reports and CV file. Continue?`)) return;
    await api.delete(`/admin/leads/${encodeURIComponent(email)}`);
    toast.success("Lead deleted");
    onChanged?.(); onClose?.();
  };
  if (!lead) return null;
  const sessions = lead.sessions || [];
  const rev = lead.revenue || {};
  const consents = lead.consents || [];
  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="lead-detail">
        <DialogHeader>
          <DialogTitle>Lead: {lead.email}</DialogTitle>
          <DialogDescription>Created {lead.created_at?.slice(0, 10)} · Locale {lead.locale}</DialogDescription>
        </DialogHeader>
        <div className="flex items-start justify-between gap-3">
          <div>
            <div className="text-xs uppercase tracking-widest text-muted-foreground">Lead</div>
            <div className="text-lg font-semibold">{lead.first_name ? `${lead.first_name} · ` : ""}{lead.email}</div>
            <div className="text-xs text-muted-foreground">Created {lead.created_at?.slice(0, 10)} · Locale {lead.locale}</div>
          </div>
          <Button variant="destructive" size="sm" onClick={del} data-testid="lead-delete">Delete (GDPR)</Button>
        </div>

        <div className="mt-4 grid grid-cols-2 gap-3">
          <div>
            <label className="text-xs uppercase tracking-widest text-muted-foreground">Status</label>
            <Select value={status} onValueChange={setStatus}>
              <SelectTrigger data-testid="lead-status-select"><SelectValue/></SelectTrigger>
              <SelectContent>
                {["new","contacted","trial","customer","closed"].map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-xs uppercase tracking-widest text-muted-foreground">Revenue</label>
            <div className="text-sm">
              {rev.trial_started_at && <div>🎫 Trial: <span className="text-muted-foreground">{rev.trial_product}</span></div>}
              {rev.purchased_at && <div>💰 Purchase: <span className="text-muted-foreground">{rev.purchase_product} · {rev.purchase_amount || "—"}</span></div>}
              {!rev.trial_started_at && !rev.purchased_at && <div className="text-muted-foreground">No revenue events yet</div>}
            </div>
          </div>
        </div>

        <div className="mt-4">
          <label className="text-xs uppercase tracking-widest text-muted-foreground">Notes</label>
          <textarea value={notes} onChange={e => setNotes(e.target.value)}
                    className="mt-1 w-full border border-border rounded-lg p-2 text-sm min-h-[80px]"
                    data-testid="lead-notes"/>
        </div>

        <div className="mt-4">
          <div className="text-xs uppercase tracking-widest text-muted-foreground mb-2">CV / Lebenslauf</div>
          <CVPanel email={lead.email} sessions={sessions}/>
        </div>

        <div className="mt-4">
          <div className="text-xs uppercase tracking-widest text-muted-foreground mb-2">Sessions ({sessions.length})</div>
          {sessions.map((s, i) => (
            <div key={i} className="rounded-lg border border-border p-3 text-xs mb-2" data-testid={`lead-session-${i}`}>
              <div className="flex items-baseline justify-between">
                <div className="font-mono">{s.report_id}</div>
                <a href={`/report/${s.report_id}`} target="_blank" rel="noreferrer" className="text-primary text-xs hover:underline">Open report →</a>
              </div>
              <div className="mt-1 grid grid-cols-2 gap-1">
                <div>Path: <span className="font-medium">{s.profile?.career_path_id || "—"}</span></div>
                <div>Goal: <span className="font-medium">{s.profile?.goal || "—"}</span></div>
                <div>Score: <span className="font-medium">{s.match?.score ?? "—"}</span></div>
                <div>Feedback: <span className="font-medium">{s.report_feedback || "—"}</span></div>
                <div>CV: {s.profile?.cv_uploaded ? <span className="text-emerald-600 font-medium">📎 gespeichert</span> : "—"}</div>
                <div>Source: <span className="text-muted-foreground">{s.source?.utm_campaign || "direct"}</span></div>
              </div>
              {(s.funnel?.ctas_clicked || []).length > 0 && (
                <div className="mt-2 text-[10px] text-muted-foreground">CTAs: {s.funnel.ctas_clicked.map(c => c.type).join(" · ")}</div>
              )}
              {(s.profile?.professional_tasks || []).length > 0 && (
                <div className="mt-2" data-testid={`lead-session-tasks-${i}`}>
                  <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-1">Aufgaben ({s.profile.professional_tasks.length})</div>
                  <div className="flex flex-wrap gap-1">
                    {s.profile.professional_tasks.map((task, ti) => (
                      <span key={ti} className="text-[10px] rounded-full border border-border px-2 py-0.5">{task}</span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>

        <div className="mt-4">
          <div className="text-xs uppercase tracking-widest text-muted-foreground mb-2">Consents</div>
          <ul className="text-xs space-y-1">
            {consents.length === 0 && <li className="text-muted-foreground">—</li>}
            {consents.map((c, i) => (
              <li key={i}>{c.granted ? "✅" : "❌"} <span className="font-medium">{c.type}</span> · {c.timestamp?.slice(0, 10)}</li>
            ))}
          </ul>
        </div>

        <div className="mt-6 flex justify-end gap-2">
          <Button variant="outline" onClick={onClose}>Close</Button>
          <Button onClick={save} data-testid="lead-save">Save</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

/* v2.15 — Inline CV panel: PDF preview via signed URL, download button, access log */
function CVPanel({ email, sessions }) {
  const cvRefs = (sessions || [])
    .map((s, i) => ({ i, ref: (s.profile || {}).cv_file_ref }))
    .filter(x => x.ref && x.ref.file_id);
  const [signed, setSigned] = useState({});
  const [log, setLog] = useState([]);

  useEffect(() => {
    (async () => {
      const s = {};
      for (const { ref } of cvRefs) {
        try {
          const r = await api.get(`/admin/leads/${encodeURIComponent(email)}/cv/${ref.file_id}/signed`);
          s[ref.file_id] = r.data.signed_url;
        } catch {}
      }
      setSigned(s);
      try {
        const r = await api.get(`/admin/leads/${encodeURIComponent(email)}/cv-access-log`);
        setLog(r.data.items || []);
      } catch {}
    })();
    // eslint-disable-next-line
  }, [email, cvRefs.length]);

  const backendBase = (process.env.REACT_APP_BACKEND_URL || "");

  if (cvRefs.length === 0) {
    return <div className="text-sm text-muted-foreground" data-testid="cv-panel-empty">Kein Lebenslauf hochgeladen.</div>;
  }

  return (
    <div className="space-y-3" data-testid="cv-panel">
      {cvRefs.map(({ ref, i }) => {
        const url = signed[ref.file_id];
        const fullUrl = url ? `${backendBase}${url}` : null;
        const downloadUrl = fullUrl ? `${fullUrl}?download=1` : null;
        return (
          <div key={ref.file_id} className="rounded-lg border border-border p-3">
            <div className="flex items-center justify-between gap-3 mb-2">
              <div className="min-w-0">
                <div className="text-sm font-medium truncate" data-testid={`cv-filename-${i}`}>{ref.original_filename || "cv.pdf"}</div>
                <div className="text-xs text-muted-foreground">{ref.content_type} · {ref.size_bytes ? Math.round(ref.size_bytes/1024) + " KB" : ""} · SHA: {ref.sha256?.slice(0, 12)}…</div>
              </div>
              {downloadUrl && (
                <a href={downloadUrl} target="_blank" rel="noreferrer"
                   className="inline-flex items-center gap-1.5 rounded-full bg-primary text-primary-foreground px-3 py-1.5 text-xs font-semibold hover:opacity-90"
                   data-testid={`cv-download-${i}`}>
                  Download
                </a>
              )}
            </div>
            {fullUrl && ref.content_type === "application/pdf" ? (
              <iframe src={fullUrl} title={`CV ${i}`} className="w-full h-96 border border-border rounded"
                      data-testid={`cv-preview-${i}`}/>
            ) : (
              <div className="text-xs text-muted-foreground">DOCX Vorschau nicht verfügbar — bitte „Download" klicken.</div>
            )}
          </div>
        );
      })}
      {log.length > 0 && (
        <div className="mt-3">
          <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-1">CV-Zugriffe (letzte {log.length})</div>
          <ul className="text-[11px] space-y-0.5">
            {log.slice(0, 5).map((e, k) => (
              <li key={k} className="font-mono text-muted-foreground">{e.at?.slice(0, 19).replace("T", " ")} — {e.action}</li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}




function ReImportPanel({ onDone }) {
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);
  async function run() {
    setRunning(true);
    try {
      const { data } = await api.post("/admin/import?force=true", {});
      setResult(data);
      onDone?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Import failed");
    } finally { setRunning(false); }
  }
  return (
    <div>
      <div className="rounded-xl border border-border bg-card p-6">
        <div className="text-sm font-semibold">Re-Import v2 dataset</div>
        <p className="mt-2 text-xs text-muted-foreground">
          Re-reads all 9 JSON files from <code className="text-foreground">/app/data_v2/</code>, replaces all reference collections, then runs the validation report.
          Existing analytics, admin accounts, assessments and reports are preserved.
        </p>
        <Button className="mt-4" data-testid="reimport-run" onClick={run} disabled={running}>
          {running ? <><Loader2 className="h-4 w-4 mr-2 animate-spin"/>Running…</> : <><RefreshCw className="h-4 w-4 mr-2"/>Re-Import & Validate</>}
        </Button>
      </div>
      {result && (
        <div className="mt-6 grid gap-4">
          <div className="rounded-xl border border-border p-4 bg-card">
            <div className="text-xs uppercase tracking-widest text-muted-foreground">Import counts</div>
            <div className="mt-2 grid grid-cols-2 md:grid-cols-5 gap-2 text-xs">
              {Object.entries(result.import.counts || {}).map(([k, v]) => (
                <div key={k} className="rounded border border-border px-2 py-1"><span className="text-muted-foreground">{k}</span> <b className="ml-1">{v}</b></div>
              ))}
            </div>
          </div>
          <Validation data={result.validation}/>
        </div>
      )}
    </div>
  );
}

function DataTable({ collection, rows, onEdit, onApprove }) {
  if (!rows || rows.length === 0) return <div className="text-sm text-muted-foreground">No records</div>;
  // Column selection per collection
  const COLS = {
    sap_module: ["id", "name", "sap_area", "status", "review_status"],
    career_path: ["id", "module_id", "specialization", "active", "review_status"],
    certification: ["id", "exam_code", "career_path_id", "deployment", "level", "verification", "review_status"],
    course: ["id", "sap_code", "title", "career_path_id", "certification_id", "status", "landing_url", "review_status"],
    unit: ["id", "course_id", "unit_no", "title", "duration_min", "skill_match_method", "review_status"],
    skill: ["id", "canonical_skill", "skill_category", "career_path_id", "importance_weight", "review_status"],
    profession_mapping: ["id", "profession_family", "profession_name_de", "primary_career_path_id", "realistic_starting_level", "review_status"],
    job_role: ["id", "title_de", "career_path_id", "level", "skill_match_method", "review_status"],
    employer_example: ["id", "company_name", "career_path_id", "level", "industry", "entry_type", "review_status"],
    level_profile: ["id", "career_path_id", "track", "review_status"],
    freelancer_rate: ["id", "career_path_id", "label", "hourly_rate_eur_min", "hourly_rate_eur_max", "display_text", "review_status"],
    ai_suggestions: ["id", "kind", "review_status", "reviewed_at"],
  }[collection] || Object.keys(rows[0]).slice(0, 6);

  return (
    <div className="overflow-x-auto rounded-xl border border-border">
      <table className="w-full text-xs">
        <thead className="bg-muted">
          <tr>
            {COLS.map(c => <th key={c} className="text-left px-3 py-2 font-semibold whitespace-nowrap">{c}</th>)}
            <th className="text-right px-3 py-2 sticky right-0 bg-muted">actions</th>
          </tr>
        </thead>
        <tbody>
          {rows.slice(0, 500).map((r, i) => (
            <tr key={i} className={`border-t border-border hover:bg-muted/40 ${r.skill_match_method === "keyword_auto" ? "bg-amber-500/5" : ""}`}>
              {COLS.map(c => (
                <td key={c} className="px-3 py-2 align-top max-w-[260px] truncate">
                  {c === "review_status" ? <StatusBadge value={r[c]}/> : renderCell(r[c])}
                </td>
              ))}
              <td className="px-3 py-2 text-right sticky right-0 bg-card whitespace-nowrap">
                <button data-testid={`edit-${r.id}`} className="text-xs px-2 py-1 rounded border border-border hover:bg-muted mr-1" onClick={() => onEdit(r)}>Edit</button>
                {r.review_status === "generated" && (
                  <button data-testid={`approve-${r.id}`} className="text-xs px-2 py-1 rounded border border-emerald-500/40 text-emerald-700 dark:text-emerald-300 hover:bg-emerald-500/10"
                          onClick={() => onApprove(r.id)}>
                    <CheckCircle2 className="inline h-3 w-3 mr-1"/>Approve
                  </button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <div className="p-2 text-[10px] text-muted-foreground">Showing first {Math.min(rows.length, 500)} of {rows.length}</div>
    </div>
  );
}

function StatusBadge({ value }) {
  const map = { generated: "amber", approved: "emerald", rejected: "rose" };
  const tone = map[value] || "gray";
  const cls = {
    amber: "border-amber-500/40 bg-amber-500/10 text-amber-700 dark:text-amber-300",
    emerald: "border-emerald-500/40 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300",
    rose: "border-rose-500/40 bg-rose-500/10 text-rose-700 dark:text-rose-300",
    gray: "border-border text-muted-foreground",
  }[tone];
  return <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] uppercase tracking-widest ${cls}`}>{value || "—"}</span>;
}

function renderCell(v) {
  if (v === null || v === undefined) return "—";
  if (typeof v === "boolean") return v ? "✓" : "✗";
  if (Array.isArray(v)) return v.length ? `[${v.length}]` : "—";
  if (typeof v === "object") return "{…}";
  return String(v);
}

/* ============ Edit dialog ============ */
function EditDialog({ collection, row, onClose, onSaved }) {
  const [patch, setPatch] = useState({});
  const [saving, setSaving] = useState(false);

  // Build editable form fields — every scalar and array field, plus embedded topics for certifications
  const fields = useMemo(() => Object.entries(row).filter(([k]) => !["id", "updated_at", "updated_by", "reviewed_at", "reviewed_by"].includes(k)), [row]);

  const current = { ...row, ...patch };

  function set(k, v) { setPatch(p => ({ ...p, [k]: v })); }

  async function save() {
    if (Object.keys(patch).length === 0) { onClose(); return; }
    setSaving(true);
    try {
      await api.patch(`/admin/${collection}/${row.id}`, patch);
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  }

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <span className="text-[10px] uppercase tracking-widest text-muted-foreground">{collection}</span>
            <span>{row.canonical_skill || row.name || row.title || row.title_de || row.profession_name_de || row.company_name || row.id}</span>
          </DialogTitle>
          <DialogDescription className="text-[10px] font-mono">{row.id}</DialogDescription>
        </DialogHeader>

        <div className="grid gap-4 mt-4">
          {fields.map(([k, v]) => (
            <FieldEditor key={k} name={k} value={current[k]} onChange={(vv) => set(k, vv)} />
          ))}
        </div>

        <DialogFooter className="mt-6">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button data-testid="edit-save" onClick={save} disabled={saving}>
            {saving ? <><Loader2 className="h-4 w-4 mr-2 animate-spin"/>Saving…</> : "Save changes"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ============ Field editor ============ */
function FieldEditor({ name, value, onChange }) {
  // Referenced fields → typeahead / tag input
  const refColl = REF_FIELDS[name];

  // Booleans
  if (typeof value === "boolean") {
    return (
      <label className="flex items-center gap-3">
        <input type="checkbox" checked={value} onChange={e => onChange(e.target.checked)} className="h-4 w-4"/>
        <span className="text-sm font-medium">{name}</span>
      </label>
    );
  }

  // Certification topics (nested array of objects)
  if (name === "topics" && Array.isArray(value)) {
    return <NestedTopicsEditor value={value} onChange={onChange}/>;
  }

  // Arrays
  if (Array.isArray(value)) {
    if (refColl) {
      return <RefTagInput label={name} refColl={refColl} value={value} onChange={onChange}/>;
    }
    return <ArrayInput label={name} value={value} onChange={onChange}/>;
  }

  // Object (rare) → JSON textarea
  if (value && typeof value === "object") {
    return <JsonField label={name} value={value} onChange={onChange}/>;
  }

  // Reference to single doc (typeahead)
  if (refColl) {
    return <RefSingleInput label={name} refColl={refColl} value={value ?? ""} onChange={onChange}/>;
  }

  // Long text
  if (typeof value === "string" && value.length > 100) {
    return (
      <div>
        <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">{name}</Label>
        <Textarea rows={4} className="mt-1 text-sm" value={value ?? ""} onChange={e => onChange(e.target.value)}/>
      </div>
    );
  }

  // Enum-like: review_status, status, deployment, level, entry_type, verification, skill_match_method
  const ENUMS = {
    review_status: ["generated", "approved", "rejected"],
    status: ["published", "draft", "planned", "reference_only", "live"],
    deployment: ["private_cloud", "public_cloud", "on_premise"],
    verification: ["sap_learning_confirmed", "unverified", "sap_page_available"],
    skill_match_method: ["keyword_auto", "manual", "quiz_content", "review_status"],
  };
  if (ENUMS[name]) {
    return (
      <div>
        <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">{name}</Label>
        <select className="mt-1 w-full h-10 rounded-md border border-border bg-card px-3 text-sm"
                value={value ?? ""} onChange={e => onChange(e.target.value || null)}>
          <option value="">—</option>
          {ENUMS[name].map(o => <option key={o} value={o}>{o}</option>)}
        </select>
      </div>
    );
  }

  // Number
  if (typeof value === "number") {
    return (
      <div>
        <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">{name}</Label>
        <Input type="number" className="mt-1" value={value ?? ""} onChange={e => onChange(e.target.value === "" ? null : Number(e.target.value))}/>
      </div>
    );
  }

  // Default: string
  return (
    <div>
      <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">{name}</Label>
      <Input className="mt-1" value={value ?? ""} onChange={e => onChange(e.target.value)}/>
    </div>
  );
}

/* ============ Array (plain strings) ============ */
function ArrayInput({ label, value, onChange }) {
  const [txt, setTxt] = useState("");
  const arr = Array.isArray(value) ? value : [];
  function add() {
    const v = txt.trim();
    if (!v) return;
    onChange([...arr, v]);
    setTxt("");
  }
  function remove(i) { onChange(arr.filter((_, k) => k !== i)); }
  return (
    <div>
      <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">{label} <span className="ml-1 opacity-60">({arr.length})</span></Label>
      <div className="mt-2 flex flex-wrap gap-1">
        {arr.map((v, i) => (
          <span key={i} className="inline-flex items-center gap-1 rounded-full border border-border bg-card px-2 py-0.5 text-xs">
            {String(v)}
            <button onClick={() => remove(i)} className="text-muted-foreground hover:text-rose-500"><X className="h-3 w-3"/></button>
          </span>
        ))}
      </div>
      <div className="mt-2 flex gap-2">
        <Input className="text-sm" value={txt} onChange={e => setTxt(e.target.value)} onKeyDown={e => { if (e.key === "Enter") { e.preventDefault(); add(); } }}/>
        <Button variant="outline" size="sm" onClick={add}>+</Button>
      </div>
    </div>
  );
}

/* ============ Reference typeahead (single) ============ */
function RefSingleInput({ label, refColl, value, onChange }) {
  const [q, setQ] = useState("");
  const [opts, setOpts] = useState([]);
  const [showList, setShowList] = useState(false);

  useEffect(() => {
    let stop = false;
    (async () => {
      try {
        const { data } = await api.get(`/admin/${refColl}/_typeahead?q=${encodeURIComponent(q)}`);
        if (!stop) setOpts(data || []);
      } catch { /* ignore */ }
    })();
    return () => { stop = true; };
  }, [q, refColl]);

  return (
    <div>
      <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">
        {label} <span className="ml-1 opacity-60">→ {refColl}</span>
      </Label>
      <div className="mt-1 relative">
        <Input value={value ?? ""} onChange={e => { onChange(e.target.value); setQ(e.target.value); setShowList(true); }}
               onFocus={() => setShowList(true)} onBlur={() => setTimeout(() => setShowList(false), 150)}
               placeholder={`select ${refColl} id…`}/>
        {showList && opts.length > 0 && (
          <div className="absolute z-10 mt-1 w-full max-h-56 overflow-auto rounded-md border border-border bg-popover shadow-lg">
            {opts.map(o => (
              <button key={o.id} type="button" onMouseDown={(e) => { e.preventDefault(); onChange(o.id); setShowList(false); }}
                      className="block w-full text-left px-3 py-1.5 text-xs hover:bg-muted">
                <span className="font-mono text-[10px] text-muted-foreground mr-2">{o.id}</span>{o.label}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

/* ============ Reference tag input (array) ============ */
function RefTagInput({ label, refColl, value, onChange }) {
  const arr = Array.isArray(value) ? value : [];
  const [q, setQ] = useState("");
  const [opts, setOpts] = useState([]);
  useEffect(() => {
    let stop = false;
    (async () => {
      try {
        const { data } = await api.get(`/admin/${refColl}/_typeahead?q=${encodeURIComponent(q)}`);
        if (!stop) setOpts(data || []);
      } catch { /* ignore */ }
    })();
    return () => { stop = true; };
  }, [q, refColl]);

  function addId(id) {
    if (!id || arr.includes(id)) return;
    onChange([...arr, id]);
    setQ("");
  }
  function remove(id) { onChange(arr.filter(x => x !== id)); }

  return (
    <div>
      <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">
        {label} <span className="ml-1 opacity-60">→ {refColl} · {arr.length}</span>
      </Label>
      <div className="mt-2 flex flex-wrap gap-1">
        {arr.map((id) => (
          <span key={id} className="inline-flex items-center gap-1 rounded-full border border-border bg-card px-2 py-0.5 text-xs font-mono">
            {id}
            <button onClick={() => remove(id)} className="text-muted-foreground hover:text-rose-500"><X className="h-3 w-3"/></button>
          </span>
        ))}
      </div>
      <div className="mt-2 relative">
        <Input value={q} onChange={e => setQ(e.target.value)} placeholder={`add ${refColl} id…`}/>
        {opts.length > 0 && q && (
          <div className="absolute z-10 mt-1 w-full max-h-56 overflow-auto rounded-md border border-border bg-popover shadow-lg">
            {opts.filter(o => !arr.includes(o.id)).map(o => (
              <button key={o.id} type="button" onMouseDown={(e) => { e.preventDefault(); addId(o.id); }}
                      className="block w-full text-left px-3 py-1.5 text-xs hover:bg-muted">
                <span className="font-mono text-[10px] text-muted-foreground mr-2">{o.id}</span>{o.label}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

/* ============ Nested topics editor (certifications) ============ */
function NestedTopicsEditor({ value, onChange }) {
  function setAt(i, k, v) { onChange(value.map((t, idx) => idx === i ? { ...t, [k]: v } : t)); }
  function remove(i) { onChange(value.filter((_, idx) => idx !== i)); }
  return (
    <div>
      <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">topics <span className="ml-1 opacity-60">({value.length})</span></Label>
      <div className="mt-2 space-y-3">
        {value.map((t, i) => (
          <div key={i} className="rounded-lg border border-border p-3 bg-muted/30">
            <div className="flex justify-between gap-2">
              <div className="text-[10px] font-mono text-muted-foreground">topic #{i + 1}</div>
              <button className="text-xs text-rose-500 hover:underline" onClick={() => remove(i)}>remove</button>
            </div>
            <div className="mt-2 grid grid-cols-2 gap-2">
              <div><Label className="text-[10px]">name</Label><Input className="mt-1 h-8 text-xs" value={t.name ?? ""} onChange={e => setAt(i, "name", e.target.value)}/></div>
              <div><Label className="text-[10px]">weighting</Label><Input className="mt-1 h-8 text-xs" value={t.weighting ?? ""} onChange={e => setAt(i, "weighting", e.target.value)}/></div>
            </div>
            <div className="mt-2">
              <RefTagInput label="skill_ids" refColl="skill" value={t.skill_ids || []} onChange={(v) => setAt(i, "skill_ids", v)}/>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function JsonField({ label, value, onChange }) {
  const [txt, setTxt] = useState(() => JSON.stringify(value, null, 2));
  return (
    <div>
      <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">{label} (JSON)</Label>
      <Textarea rows={6} className="mt-1 font-mono text-xs" value={txt}
                onChange={e => { setTxt(e.target.value); try { onChange(JSON.parse(e.target.value)); } catch {} }}/>
    </div>
  );
}
